import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const success = await register(email, password, fullName);
      if (success) {
        navigate('/dashboard');
      } else {
        setError(t('auth.registerError'));
      }
    } catch {
      setError(t('auth.registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-cream-200 p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gold-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-store-2-line text-3xl text-gold-500" />
          </div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black mb-1">
            {t('app.title')}
          </h1>
          <p className="text-sm text-boutique-warm">{t('app.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-error-warning-line" />
              <span>{error}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-boutique-black mb-2">
              {t('auth.fullName')}
            </label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
              placeholder="Adınız Soyadınız"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-boutique-black mb-2">
              {t('auth.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
              placeholder="admin@butik.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-boutique-black mb-2">
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
              placeholder="******"
              required
              minLength={6}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-boutique-black text-white rounded-lg font-medium text-sm hover:bg-boutique-charcoal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                {t('common.loading')}
              </span>
            ) : (
              t('auth.register')
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-boutique-warm">
            {t('auth.hasAccount')}{' '}
            <a href="/login" className="text-gold-600 font-medium hover:text-gold-500 transition-colors">
              {t('auth.login')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}